import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import AxiosConfig from '../../../../../../config/AxiosConfig';
import UserMentionManager from '../../../../../helpers/utils/UserMentionManager';
import Dropzone from '../../../../utils/common/Dropzone';
import LoadingButton from '../../../../utils/common/LoadingButton';
import TextFieldWithMentions from '../../../../utils/common/TextFieldWithMentions';
import UserAssignmentAutocomplete from '../../../../utils/common/UserAssignmentAutocomplete';

const AddTask = ({
  handleDisplayAddTask,
  ticket,
  mutateTicket,
  mutateTask,
}) => {
  const profile = useSelector((state) => state?.login?.profile);
  const actions = useSelector((state) => state?.static?.actions);
  const services = useSelector((state) => state?.static?.services);
  const filteredActions = useMemo(
    () => actions?.filter((action) => !action.isDeprecated),
    [actions]
  );
  const filteredServices = useMemo(
    () =>
      services?.filter((service) =>
        ['SAV', 'SLO', 'SCO'].includes(service.code)
      ),
    [services]
  );
  const noteTypes = useSelector((state) => state?.static?.noteTypes);
  const [currentNote, setCurrentNote] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsSize, setTotalAttachmentsSize] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: mutateUsers } = useSWRConfig();
  const userMentionManager = new UserMentionManager();

  useEffect(() => {
    if (filteredActions.length > 0 && !selectedAction) {
      setSelectedAction(filteredActions[0]?._id);
    }
  }, [filteredActions, selectedAction]);

  // useEffect(() => {
  //   if (filteredServices.length > 0 && !selectedService)
  //     setSelectedService(filteredServices[0]?._id);
  // }, [filteredServices, selectedService]);

  useEffect(() => {
    if (!currentNote) {
      AxiosConfig.create('post', `/notes/`)
        .call({
          project: ticket?.project?._id,
          ticket: ticket?._id,
          type: noteTypes.find((noteType) => noteType?.code === 'TASKCREATED')
            ?._id,
          user: profile?._id,
        })
        .exit((res) => setCurrentNote(res?.data))
        .catch((err) => console.error(err));
    }
  }, [ticket, noteTypes, profile]);

  useEffect(() => {
    for (const att of attachments) {
      if (!att?._id) {
        createAttachment(att);
      }
    }
  }, [attachments]);

  useEffect(() => {
    console.log('changed by add task');
    setAttachments(currentNote?.attachments || []);
  }, [currentNote]);

  const handleActionChange = (event) => setSelectedAction(event.target.value);
  const handleServiceChange = (event) => setSelectedService(event.target.value);

  const handleTaskConfirm = () => {
    if (currentNote && selectedAction && selectedService) {
      setIsLoading(true);
      const action = filteredActions.find((a) => a._id === selectedAction);
      const data = {
        userId: profile?._id,
        ticketId: ticket?._id,
        actionId: selectedAction,
        serviceId: selectedService,
        noteContent: userMentionManager.replaceNamesWithIds(noteContent),
        assignedBy: profile?._id,
        assignedTo: selectedUser?._id || null,
      };
      if (action?.isScheduled && scheduledDate) {
        data.scheduledAt = scheduledDate.toISOString();
      }
      AxiosConfig.create('post', `/tasks`)
        .call(data)
        .exit((res) => {
          setCurrentNote(res?.data);
          mutateUsers(`/users/`);
          mutateTask();
          mutateTicket();
          handleDisplayAddTask();
          setIsLoading(false);
        })
        .catch((err) => console.error(err));
    }
  };

  const createAttachment = (att) => {
    AxiosConfig.create('post', `/notes/${currentNote?._id}/attachments`)
      .call({
        _id: att?._id,
        content: att?.content,
        file: {
          type: att.file?.type || att?.mimeType,
          size: att.file?.size || att?.size,
          name: att.file?.name || att?.name,
          cid: att?.cid,
          lastModifiedDate: att.file?.lastModifiedDate,
        },
        link: att?.link,
        isAtt: att?.isAtt,
      })
      .exit((res) => {
        setCurrentNote((_state) => res?.data);
        mutateTicket();
      })
      .catch((err) => console.error(err));
  };

  const deleteAttachment = (att) => {
    AxiosConfig.create(
      'delete',
      `/notes/${currentNote?._id}/attachments/${att?._id}`
    )
      .call()
      .exit((res) => {
        setCurrentNote((_state) => res?.data);
        mutateTicket();
      })
      .catch((err) => console.error(err));
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, margin: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Nouvelle tâche</Typography>
        <Button
          onClick={handleDisplayAddTask}
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
        >
          Retour
        </Button>
      </Box>
      <Box>
        {filteredActions.length > 0 && (
          <Select
            id="select-action-to-add"
            value={selectedAction}
            onChange={handleActionChange}
            sx={{ width: '95%', margin: 1 }}
            size="small"
          >
            {filteredActions.map((action) => (
              <MenuItem key={action._id} value={action._id}>
                <Typography>{action.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        )}
        {filteredServices.length > 0 && (
          <FormControl
            sx={{ width: '95%', margin: 1 }}
            size="small"
            error={!selectedService}
          >
            <Select
              id="select-service-to-add"
              value={selectedService || ''}
              onChange={handleServiceChange}
            >
              {filteredServices.map((service) => (
                <MenuItem key={service._id} value={service._id}>
                  <Typography>{service.code}</Typography>
                </MenuItem>
              ))}
            </Select>
            {!selectedService && (
              <FormHelperText>Veuillez sélectionner un service</FormHelperText>
            )}
          </FormControl>
        )}
        {filteredActions.find((a) => a._id === selectedAction)?.isScheduled ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              width: '95%',
              margin: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1">
                Date et heure de planification
              </Typography>
              <DatePicker
                selected={scheduledDate}
                onChange={(date) => setScheduledDate(date)}
                showTimeSelect
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                className="custom-datepicker"
              />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <TextFieldWithMentions
                noteContent={noteContent}
                setNoteContent={setNoteContent}
                handleTaskConfirm={handleTaskConfirm}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: '95%', margin: 1 }}>
            <TextFieldWithMentions
              noteContent={noteContent}
              setNoteContent={setNoteContent}
              handleTaskConfirm={handleTaskConfirm}
            />
          </Box>
        )}
        <Dropzone
          attachments={attachments}
          setAttachments={setAttachments}
          deleteAttachment={deleteAttachment}
          attachmentsSize={attachmentsSize}
          setTotalAttachmentsSize={setTotalAttachmentsSize}
        />
        <UserAssignmentAutocomplete
          setSelectedUser={setSelectedUser}
          selectedUser={selectedUser}
          isSubmit={true}
        />
        <LoadingButton
          isLoading={isLoading}
          onClick={handleTaskConfirm}
          disabled={
            noteContent.length === 0 || !selectedAction || !selectedService
          }
          variant="contained"
          sx={{ width: '95%', margin: 1 }}
        >
          Créer la tâche
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default AddTask;
